/**
 * Client-side preference store — pure localStorage, no backend.
 *
 * Used by `LanguageAccessibility` (the Settings page) since there is no
 * dedicated language / a11y endpoint on service-customer. Persisting locally
 * keeps the UX functional today; once a server-side preferences API ships,
 * the Settings page can hydrate this from the backend without touching the
 * read/write surface.
 *
 * Keys are namespaced under `upmp.customer.preferences` for forward-compat
 * with a profile-wide preferences endpoint (`PUT /v1/customers/:id` already
 * accepts an arbitrary `preferences: Record<string, unknown>` blob — Phase 2
 * will sync these client values into that field at sign-in).
 */
const STORAGE_KEY = 'upmp.customer.preferences';

export type LanguageCode = 'en' | 'ha' | 'yo' | 'ig' | 'pcm';
export type TextSize = 'small' | 'default' | 'large';

export interface ClientPreferences {
  readonly language: LanguageCode;
  readonly textSize: TextSize;
  readonly highContrast: boolean;
  readonly screenReader: boolean;
  readonly reducedMotion: boolean;
}

export const DEFAULT_PREFERENCES: ClientPreferences = {
  language: 'en',
  textSize: 'default',
  highContrast: false,
  screenReader: false,
  reducedMotion: false,
};

function isLanguage(value: unknown): value is LanguageCode {
  return value === 'en' || value === 'ha' || value === 'yo' || value === 'ig' || value === 'pcm';
}

function isTextSize(value: unknown): value is TextSize {
  return value === 'small' || value === 'default' || value === 'large';
}

export function readClientPreferences(): ClientPreferences {
  if (typeof window === 'undefined') {
    return DEFAULT_PREFERENCES;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_PREFERENCES;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) {
      return DEFAULT_PREFERENCES;
    }
    const obj = parsed as Record<string, unknown>;
    return {
      language: isLanguage(obj['language']) ? obj['language'] : DEFAULT_PREFERENCES.language,
      textSize: isTextSize(obj['textSize']) ? obj['textSize'] : DEFAULT_PREFERENCES.textSize,
      highContrast: typeof obj['highContrast'] === 'boolean' ? obj['highContrast'] : DEFAULT_PREFERENCES.highContrast,
      screenReader: typeof obj['screenReader'] === 'boolean' ? obj['screenReader'] : DEFAULT_PREFERENCES.screenReader,
      reducedMotion: typeof obj['reducedMotion'] === 'boolean' ? obj['reducedMotion'] : DEFAULT_PREFERENCES.reducedMotion,
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

export function writeClientPreferences(prefs: ClientPreferences): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

export function clearClientPreferences(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.removeItem(STORAGE_KEY);
}
